/**
 * Convert object to FormData with proper handling for different data types
 * @param {Object} values - Object to convert to FormData
 * @param {Object} options - Configuration options
 * @param {boolean} options.debug - Enable debug logging
 * @param {Array<string>} options.excludeEmpty - Fields to exclude when empty
 * @param {Array<string>} options.jsonFields - Fields that should be JSON stringified
 * @returns {FormData} - Formatted FormData object
 */
export function objectToFormData(values, options = {}) {
  const { debug = false, excludeEmpty = [], jsonFields = ["addresses"] } = options;

  const formData = new FormData();

  Object.entries(values).forEach(([key, value]) => {
    // Skip empty values if configured to do so
    if (excludeEmpty.includes(key) && (value === null || value === undefined || value === "")) {
      return;
    }

    // Skip null/undefined values
    if (value === null || value === undefined) {
      return;
    }

    // Handle different data types properly
    if (value instanceof File) {
      // File should be appended as-is
      formData.append(key, value);
    } else if (jsonFields.includes(key) && (Array.isArray(value) || typeof value === "object")) {
      // Serialize specified fields to JSON string
      formData.append(key, JSON.stringify(value));
    } else if (Array.isArray(value)) {
      // Handle arrays - serialize to JSON by default
      formData.append(key, JSON.stringify(value));
    } else if (typeof value === "object" && !(value instanceof File) && !(value instanceof Date)) {
      // Other objects should be serialized
      formData.append(key, JSON.stringify(value));
    } else if (value instanceof Date) {
      // Handle Date objects
      formData.append(key, value.toISOString());
    } else if (typeof value === "boolean") {
      // Handle boolean values
      formData.append(key, value.toString());
    } else if (value === "") {
      // Handle empty strings - append as is
      formData.append(key, "");
    } else {
      // Primitive values
      formData.append(key, value.toString());
    }
  });

  // Debug logging
  if (debug) {
    console.log("FormData contents:");
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
  }

  return formData;
}

/**
 * Simple version for basic use cases
 * @param {Object} values - Object to convert
 * @returns {FormData}
 */
export function simpleObjectToFormData(values) {
  return objectToFormData(values, { debug: true });
}
